import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { ConfirmDialog } from 'primevue'
import PrimeVue, { defaultOptions, type PrimeVueConfiguration } from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import DialogService from 'primevue/dialogservice'
import Drawer from 'primevue/drawer'
import Ripple from 'primevue/ripple'
import StyleClass from 'primevue/styleclass'
import Toast from 'primevue/toast'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import type { App } from 'vue'

const action = {
  50: '#EEF8FF',
  100: '#D9EEFF',
  200: '#BCE2FF',
  300: '#8ED1FF',
  400: '#59B8FF',
  500: '#3299FF',
  600: '#1C7BF4',
  700: '#1567E9',
  800: '#1750B6',
  900: '#19468F',
  950: '#142C57',
}
const success = {
  50: '#F0FEEF',
  100: '#D9FFD8',
  200: '#B6FEB4',
  300: '#7CFB7A',
  400: '#53F151',
  500: '#11D80F',
  600: '#07B306',
  700: '#098C09',
  800: '#0D6E0D',
  900: '#0D5A0E',
  950: '#003302',
}
const warning = {
  50: '#FDF9ED',
  100: '#F7E9C5',
  200: '#F1D896',
  300: '#E9BE60',
  400: '#E4A73B',
  500: '#DC8924',
  600: '#C2681D',
  700: '#A24B1B',
  800: '#843B1C',
  900: '#6D311A',
  950: '#3E180A',
}
const danger = {
  50: '#FFF1F1',
  100: '#FFDFDF',
  200: '#FFC5C5',
  300: '#FF9C9C',
  400: '#FF6464',
  500: '#FF3333',
  600: '#EE1717',
  700: '#C80D0D',
  800: '#A50F0F',
  900: '#891313',
  950: '#4B0404',
}

const FinEstHubPreset = definePreset(Aura, {
  primitive: { action, success, warning, danger },
  semantic: {
    primary: action,
    typography: { fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' },
    list: { option: { fontSize: 'inherit' }, optionGroup: { fontSize: 'inherit' } },
    navigation: { item: { label: { fontSize: 'inherit' } }, submenuLabel: { fontSize: 'inherit' } },
    formField: {
      fontSize: 'inherit',
      paddingX: 'var(--field-px)',
      paddingY: 'var(--field-py)',
      borderRadius: 'var(--field-radius)',
      invalidBorderColor: '{danger.700}',
      invalidPlaceholderColor: '{danger.700}',
      floatLabelInvalidColor: '{danger.700}',
    },
    focusRing: { width: '3px', style: 'solid', color: 'rgb(21 103 233 / 20%)', offset: '0' },
    colorScheme: {
      light: {
        formField: { borderColor: 'var(--color-line)', hoverBorderColor: '#CFD6D7', focusBorderColor: '{action.700}' },
      },
    },
  },
  components: {
    card: { root: { shadow: 'var(--shadow-card)' }, title: { fontSize: '1.25rem' } },
    dialog: { title: { fontSize: '1.25rem' } },
    drawer: { title: { fontSize: '1.5rem' } },
    progressbar: { label: { fontSize: '0.75rem' } },
    stepper: { stepNumber: { fontSize: '1.143rem' } },
    inputgroup: { addon: { background: 'var(--color-surface-2)' } },
    badge: {
      root: { fontSize: '0.75rem' },
      sm: { fontSize: '0.625rem' },
      lg: { fontSize: '0.875rem' },
      xl: { fontSize: '1rem' },
      colorScheme: {
        light: {
          info: { background: '{action.50}', color: '#1463E0' },
          success: { background: '{success.50}', color: '#0C650C' },
          warn: { background: '{warning.50}', color: '#99461A' },
          danger: { background: '{danger.50}', color: '#BE0C0C' },
        },
      },
    },
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{action.700}',
              hoverBackground: '{action.800}',
              activeBackground: '{action.800}',
              borderColor: '{action.700}',
              hoverBorderColor: '{action.800}',
              activeBorderColor: '{action.800}',
              color: '#FFFFFF',
              hoverColor: '#FFFFFF',
              activeColor: '#FFFFFF',
            },
            info: {
              background: '{action.700}',
              hoverBackground: '{action.800}',
              activeBackground: '{action.800}',
              borderColor: '{action.700}',
              hoverBorderColor: '{action.800}',
              activeBorderColor: '{action.800}',
              color: '#FFFFFF',
              hoverColor: '#FFFFFF',
              activeColor: '#FFFFFF',
            },
            success: {
              background: '{success.800}',
              hoverBackground: '{success.900}',
              activeBackground: '{success.900}',
              borderColor: '{success.800}',
              hoverBorderColor: '{success.900}',
              activeBorderColor: '{success.900}',
              color: '#FFFFFF',
              hoverColor: '#FFFFFF',
              activeColor: '#FFFFFF',
            },
            danger: {
              background: '{danger.700}',
              hoverBackground: '{danger.800}',
              activeBackground: '{danger.800}',
              borderColor: '{danger.700}',
              hoverBorderColor: '{danger.800}',
              activeBorderColor: '{danger.800}',
              color: '#FFFFFF',
              hoverColor: '#FFFFFF',
              activeColor: '#FFFFFF',
            },
            warn: {
              background: '{warning.50}',
              hoverBackground: '{warning.100}',
              activeBackground: '{warning.200}',
              borderColor: '{warning.50}',
              hoverBorderColor: '{warning.100}',
              activeBorderColor: '{warning.200}',
              color: '#99461A',
              hoverColor: '#99461A',
              activeColor: '#99461A',
            },
          },
          outlined: {
            info: {
              hoverBackground: '{action.50}',
              activeBackground: '{action.100}',
              borderColor: '{action.200}',
              color: '{action.700}',
            },
            success: {
              hoverBackground: '{success.50}',
              activeBackground: '{success.100}',
              borderColor: '{success.200}',
              color: '{success.800}',
            },
            danger: {
              hoverBackground: '{danger.50}',
              activeBackground: '{danger.100}',
              borderColor: '{danger.200}',
              color: '{danger.700}',
            },
          },
          text: {
            info: { hoverBackground: '{action.50}', activeBackground: '{action.100}', color: '{action.700}' },
            success: { hoverBackground: '{success.50}', activeBackground: '{success.100}', color: '{success.800}' },
            danger: { hoverBackground: '{danger.50}', activeBackground: '{danger.100}', color: '{danger.700}' },
          },
        },
      },
    },
    inlinemessage: {
      colorScheme: {
        light: {
          info: { background: '{action.50}', borderColor: '{action.200}', color: '#1463E0' },
          success: { background: '{success.50}', borderColor: '{success.200}', color: '#0C650C' },
          warn: { background: '{warning.50}', borderColor: '{warning.200}', color: '#99461A' },
          error: { background: '{danger.50}', borderColor: '{danger.200}', color: '#BE0C0C' },
        },
      },
    },
    message: {
      text: { fontSize: '1rem', sm: { fontSize: '0.875rem' }, lg: { fontSize: '1.125rem' } },
      colorScheme: {
        light: {
          info: { background: '{action.50}', borderColor: '{action.200}', color: '#1463E0' },
          success: { background: '{success.50}', borderColor: '{success.200}', color: '#0C650C' },
          warn: { background: '{warning.50}', borderColor: '{warning.200}', color: '#99461A' },
          error: { background: '{danger.50}', borderColor: '{danger.200}', color: '#BE0C0C' },
        },
      },
    },
    tag: {
      colorScheme: {
        light: {
          info: { background: '{action.50}', color: '#1463E0' },
          success: { background: '{success.50}', color: '#0C650C' },
          warn: { background: '{warning.50}', color: '#99461A' },
          danger: { background: '{danger.50}', color: '#BE0C0C' },
        },
      },
    },
    toast: {
      root: { borderRadius: '1rem', borderWidth: '1px' },
      summary: { fontSize: '1rem' },
      detail: { fontSize: '0.875rem' },
      colorScheme: {
        light: {
          info: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-line)',
            color: 'var(--color-ink)',
            detailColor: 'var(--color-ink-2)',
            shadow: 'var(--shadow-dropdown)',
          },
          success: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-line)',
            color: 'var(--color-ink)',
            detailColor: 'var(--color-ink-2)',
            shadow: 'var(--shadow-dropdown)',
          },
          warn: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-line)',
            color: 'var(--color-ink)',
            detailColor: 'var(--color-ink-2)',
            shadow: 'var(--shadow-dropdown)',
          },
          error: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-line)',
            color: 'var(--color-ink)',
            detailColor: 'var(--color-ink-2)',
            shadow: 'var(--shadow-dropdown)',
          },
        },
      },
    },
  },
})

export const primevueLocales = {
  en: {
    firstDayOfWeek: 1,
    dateFormat: 'dd/mm/yy',
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    fileSizeTypes: defaultOptions.locale?.fileSizeTypes || [],
    today: 'Today',
    clear: 'Clear',
  },
  de: {
    firstDayOfWeek: 1,
    dateFormat: 'dd.mm.yy',
    monthNames: [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ],
    monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
    dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    fileSizeTypes: defaultOptions.locale?.fileSizeTypes || [],
    today: 'Heute',
    clear: 'Löschen',
  },
}

const config: PrimeVueConfiguration = {
  ripple: true,
  inputVariant: 'outlined',
  pt: { dialog: { root: 'w-full max-w-xl' }, confirmdialog: { root: 'w-full max-w-xl' } },
  theme: { preset: FinEstHubPreset, options: { darkModeSelector: false } },
  locale: { ...defaultOptions.locale, ...primevueLocales.de },
}
let installedConfig: PrimeVueConfiguration | null = null
export function setPrimeVueLocale(locale: keyof typeof primevueLocales) {
  if (installedConfig) installedConfig.locale = { ...installedConfig.locale, ...primevueLocales[locale] }
}
export default (app: App<Element>) => {
  app
    .use(PrimeVue, config)
    .use(ToastService)
    .use(DialogService)
    .use(ConfirmationService)
    .component('Toast', Toast)
    .component('ConfirmDialog', ConfirmDialog)
    .component('Drawer', Drawer)
    .directive('tooltip', Tooltip)
    .directive('ripple', Ripple)
    .directive('styleclass', StyleClass)
  installedConfig = app.config.globalProperties.$primevue.config
}
